import { workedVisitsCard } from "./worked-visits";
import type { ScreenContext } from "../../shell/types";
import { list } from "../../ui/list";
import type { PageInfo } from "../../../server/rules/list-page";
import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { openModal } from "../../ui/modal";
import { card, chip, ib, table } from "../../ui/record";
import { area, field, refuse, select } from "../clients/forms";
import { pageHead } from "../empty";
import { payRatesCard } from "./pay-rates";

// Home Care › Payroll (brief 1E-4, mockup v56 homecare/payroll payrollTable): the week's run with
// the stages Hours locked, Review, Sent to payroll, Paid; the table Worker, Type, Where, Hours,
// Rate, Regular, OT, Bonus, Reimbursements, Gross; Send to payroll. The run builds itself when the
// week closes and can be rebuilt until the hours are locked; in review a person is held with a
// reason (their lines move to the next run); Approve needs the approve-payroll switch; Send to
// payroll exports the file in the chosen layout (kept on the run, and downloaded); Mark paid.
// Brief 1E-5 (mockup v58): the Payroll file card (FlowCare's standard file by default, its columns,
// pick a layout, Add a layout by renaming, reordering and dropping columns) and the Mileage card
// (paid to staff from a date with the IRS rate for reference; charged to clients, set per client).
// Brief 1E-11 (mockup v61): the Week summary per person for the week shown (patients, visits,
// clocked hours, miles between patients, earned, mileage, average per hour) with its two checks,
// past the overtime threshold and under the minimum wage; a row opens the person's week by day;
// Download (CSV). Only with Payroll at View and Sees financials. Paid time off and holiday pay show
// as rows of their own on the run.

interface Row {
  personId: string;
  worker: string;
  name: string;
  type: string;
  where: string;
  notes: string[];
  hours: string;
  rate: string;
  regular: string;
  overtime: string;
  bonus: string;
  reimbursements: string;
  adjustments: string | null;
  gross: string;
  held: { reason: string } | null;
}
interface Run {
  week: { start: string; end: string; label: string };
  ended: boolean;
  stages: string[];
  period: {
    id: string;
    status: "open" | "locked" | "approved" | "exported" | "paid";
    stage: number;
    words: string;
    built: string;
    exported: { id: string; name: string } | null;
  } | null;
  rows: Row[];
  people: { id: string; name: string; held: boolean }[];
  totals: { gross: string; regularHours: string; overtimeHours: string; held: number };
  layouts: { id: string; name: string }[];
  layoutId: string | null;
  fileLayout: {
    name: string;
    columns: { header: string; source: string; what: string }[];
    rows: string;
  } | null;
  mileage: string;
  mileageCard: { rate: string; from: string; irs: string };
  overtime: string;
  can: {
    build: boolean;
    lock: boolean;
    hold: boolean;
    approve: boolean;
    export: boolean;
    paid: boolean;
    editMileage: boolean;
    addLayout: boolean;
  };
  file?: { id: string; name: string; csv: string };
}

interface Summary {
  week: { start: string; end: string; label: string };
  minimumWage: string | null;
  afterHours: number;
  rows: {
    personId: string;
    name: string;
    title: string;
    patients: number;
    visits: number;
    clocked: string;
    miles: string;
    earned: string;
    mileage: string;
    average: string;
    pastThreshold: string;
    threshold: number;
    checks: { kind: "over" | "under"; words: string }[];
    shortfall: string | null;
    days: {
      day: string;
      patients: string;
      visits: number;
      clocked: string;
      hours: string;
      miles: string;
      earned: string;
    }[];
  }[];
}

const kv = (pairs: [string, string][]) =>
  el("dl", { class: "kv" }, ...pairs.flatMap(([k, v]) => [el("dt", {}, k), el("dd", {}, v)]));

/** A row's week by day (mockup v61 dayModal). */
function weekModal(v: Summary, r: Summary["rows"][number]) {
  const close = el("button", { type: "button", class: "btn" }, "Close");
  const m = openModal({
    title: `${r.name} · week of ${v.week.label}`,
    sub: r.title,
    wide: true,
    body: [
      table(
        [
          { h: "Day", f: (d) => el("b", {}, d.day) },
          { h: "Patients", f: (d) => d.patients },
          { h: "Visits", num: true, f: (d) => (d.visits ? String(d.visits) : "–") },
          { h: "Clocked in and out", f: (d) => el("span", { class: "small" }, d.clocked) },
          { h: "Hours", num: true, f: (d) => d.hours },
          { h: "Miles", num: true, f: (d) => d.miles },
          { h: "Earned", num: true, f: (d) => d.earned },
        ],
        r.days,
        undefined,
        "Week by day",
      ),
      el(
        "div",
        { class: "grid2", style: "margin-top:10px" },
        kv([
          ["Clocked", r.clocked],
          [`Past ${r.threshold}`, r.pastThreshold],
          ["Average per hour", r.average],
        ]),
        kv([
          ["Earned", r.earned],
          ["Mileage", r.mileage],
          ["Minimum wage", v.minimumWage ? `$${v.minimumWage}/hr` : "Not set"],
        ]),
      ),
      r.shortfall
        ? el(
            "div",
            { class: "note", style: "margin-top:10px" },
            el(
              "p",
              {},
              el("b", {}, r.shortfall),
              " The company decides how to make it up before payroll is sent.",
            ),
          )
        : el("span"),
    ],
    actions: [close],
  });
  close.addEventListener("click", () => m.close());
}

/** The Week summary card (mockup v61), or nothing without Sees financials. */
async function summaryCard(week: string): Promise<HTMLElement | null> {
  const res = await api<Summary>("GET", `/api/payroll/week-summary?week=${week}`);
  if (!res.ok) return null;
  const v = res.body;
  const download = el("button", { type: "button", class: "btn sm" }, icon("download"), " Download");
  download.addEventListener("click", async () => {
    const r = await fetch(`/api/payroll/week-summary/file?week=${v.week.start}`, {
      cache: "no-store",
    });
    if (!r.ok) return toast("The week summary couldn't be downloaded");
    const name =
      /filename="([^"]+)"/.exec(r.headers.get("content-disposition") ?? "")?.[1] ??
      "Week summary.csv";
    saveText(name, await r.text());
    toast("Week summary downloaded", "CSV");
  });
  const section = card(
    el("span", {}, icon("people"), ` Week summary · ${v.week.label}`),
    v.minimumWage
      ? `Clocked hours and what each person earned, checked against ${v.afterHours} hours and the minimum wage ($${v.minimumWage}/hr, a company setting).`
      : `Clocked hours and what each person earned, checked against ${v.afterHours} hours. The minimum wage is set in Settings › Week, overtime & PTO.`,
    [download],
    v.rows.length
      ? table(
          [
            {
              h: "Person",
              f: (r) =>
                el(
                  "span",
                  { "data-wks": r.name },
                  el("b", {}, r.name),
                  el("div", { class: "small muted" }, r.title),
                ),
            },
            { h: "Patients", num: true, f: (r) => String(r.patients) },
            { h: "Visits", num: true, f: (r) => String(r.visits) },
            { h: "Clocked", num: true, f: (r) => r.clocked },
            { h: "Miles between patients", num: true, f: (r) => r.miles },
            { h: "Earned", num: true, f: (r) => r.earned },
            { h: "Mileage", num: true, f: (r) => r.mileage },
            { h: "Average per hour", num: true, f: (r) => el("b", {}, r.average) },
            {
              h: "Checks",
              f: (r) =>
                r.checks.length
                  ? el(
                      "span",
                      { class: "wkchecks" },
                      ...r.checks.map((c) => chip(c.words, c.kind === "over" ? "warn" : "bad")),
                    )
                  : chip("OK", "ok"),
            },
          ],
          v.rows,
          (r) => weekModal(v, r),
          "Week summary",
        )
      : el("p", { class: "muted" }, "No validated visits in this week yet."),
    el(
      "p",
      { class: "small muted", style: "margin:8px 0 0" },
      "Mileage is a reimbursement and isn't counted in the average per hour.",
    ),
  );
  section.setAttribute("aria-label", "Week summary");
  // Fits a 1280-wide desktop without scrolling sideways: headings and names wrap, checks stack.
  section.querySelector(".tw")?.classList.add("wks");
  return section;
}

const plus = (day: string, n: number) =>
  new Date(Date.parse(`${day}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);

/** Where the run is, as the stage card's heading says it. */
const HEADING: Record<string, string> = {
  open: "Open",
  locked: "Review",
  approved: "Approved",
  exported: "Sent to payroll",
  paid: "Paid",
};

/** What the stage card says about the next step. */
const NEXT: Record<string, string> = {
  none: "The run builds itself the morning after the week ends, from validated visits.",
  open: "Check the hours, then lock them. Until then the run can be rebuilt.",
  locked: "Review: hold anyone who shouldn't be paid yet, with a reason. Then approve.",
  approved: "Send the file to the payroll company. They run payroll.",
  exported: "Sent to payroll. Mark it paid once the payroll company has paid it.",
  paid: "Paid.",
};

function saveText(name: string, text: string) {
  const a = el("a", {
    href: URL.createObjectURL(new Blob([text], { type: "text/csv" })),
    download: name,
  });
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

export async function payrollScreen(host: HTMLElement, ctx: ScreenContext) {
  // A link can open a given week: /homecare/payroll?week=YYYY-MM-DD.
  const asked = new URLSearchParams(location.search).get("week");
  let week: string | null = asked && /^\d{4}-\d{2}-\d{2}$/.test(asked) ? asked : null;
  const head = pageHead(
    ctx,
    "Approved time from every client becomes one run each pay period, with bonuses and reimbursements.",
    [],
  );
  const body = el("div", { class: "ws" });
  // Brief 1E-16 addition (mockup v74): Pay rates, each title's pay rate card, under the head.
  const payRates = el("div");
  // Brief 1E-33: worked visits, starting on Not sent.
  host.append(head, workedVisitsCard(ctx, "payroll"), payRates, body);
  void payRatesCard().then((c) => c && payRates.replaceChildren(c));

  let seq = 0;
  async function load() {
    const mine = ++seq;
    const res = await api<Run>("GET", `/api/payroll${week ? `?week=${week}` : ""}`);
    if (mine !== seq) return;
    if (!res.ok) {
      body.replaceChildren(
        el("p", { class: "note", role: "alert" }, "Payroll couldn't be loaded."),
      );
      return;
    }
    draw(res.body);
  }

  async function step(path: string, done: string, sub?: string, data: unknown = {}) {
    const r = await api<Run>("POST", path, data);
    if (!r.ok) return toast("That didn't go through", r.body.error);
    toast(done, sub);
    draw(r.body);
    return r.body;
  }

  function sendToPayroll(v: Run) {
    const p = v.period;
    if (!p) return;
    const pick = select(
      "Payroll file layout",
      v.layouts.map((l) => [l.id, l.name] as const),
      v.layoutId,
    );
    const go = el("button", { type: "button", class: "btn pri" }, icon("download"), " Export");
    const m = openModal({
      title: "Send to payroll",
      sub: `${v.week.label} · held people are left out and paid when released. The file is kept on this run.`,
      body: [el("div", { class: "form", style: "max-width:none" }, pick.el)],
      actions: [go],
    });
    go.addEventListener("click", async () => {
      go.disabled = true;
      const r = await api<Run>("POST", `/api/payroll/${p.id}/export`, {
        layoutId: pick.input.value,
      });
      go.disabled = false;
      if (!r.ok) return refuse(m.note, r.body.error);
      m.close();
      if (r.body.file) saveText(r.body.file.name, r.body.file.csv);
      toast("Payroll file exported", r.body.file?.name);
      draw(r.body);
    });
  }

  function holdModal(v: Run, row: Row) {
    const p = v.period;
    if (!p) return;
    const why = area(`Why is ${row.name} held?`, "");
    const go = el("button", { type: "button", class: "btn pri" }, "Hold");
    const m = openModal({
      title: `Hold ${row.name}`,
      sub: "Their lines stay off this run's file and move to the next run, with the reason given.",
      body: [el("div", { class: "form", style: "max-width:none" }, why.el)],
      actions: [go],
    });
    why.input.focus();
    go.addEventListener("click", async () => {
      go.disabled = true;
      const r = await api<Run>("POST", `/api/payroll/${p.id}/hold`, {
        personId: row.personId,
        reason: why.input.value,
      });
      go.disabled = false;
      if (!r.ok) return refuse(m.note, r.body.error);
      m.close();
      toast(`${row.name} held`, "Their lines move to the next run");
      draw(r.body);
    });
  }

  function mileageModal() {
    const rate = field("Pay rate a mile ($)", "", { inputmode: "decimal" });
    const from = field("Starts on", plus(new Date().toISOString().slice(0, 10), 1), {
      type: "date",
    });
    const go = el("button", { type: "button", class: "btn pri" }, "Save");
    const m = openModal({
      title: "New pay rate a mile",
      sub: "A new pay rate starts on a date. Visits before it keep the pay rate on their own date.",
      body: [el("div", { class: "form", style: "max-width:none" }, rate.el, from.el)],
      actions: [go],
    });
    go.addEventListener("click", async () => {
      go.disabled = true;
      const r = await api("PUT", "/api/settings/payroll", {
        mileage: { rate: rate.input.value, from: from.input.value },
      });
      go.disabled = false;
      if (!r.ok) return refuse(m.note, (r.body as { error?: string }).error);
      m.close();
      toast("Pay rate a mile saved", "From the day picked");
      void load();
    });
  }

  /** Add a layout: start from the chosen file, then rename, reorder or drop its columns. */
  function layoutModal(v: Run) {
    const from = v.fileLayout;
    if (!from) return;
    const name = field("Layout name", "", { maxlength: "80", placeholder: "Payroll company" });
    const cols = from.columns.map((c) => ({
      source: c.source,
      keep: el("input", { type: "checkbox", checked: true, "aria-label": `Keep ${c.header}` }),
      header: el("input", {
        type: "text",
        value: c.header,
        "aria-label": `Heading for ${c.header}`,
      }),
    }));
    const list = el("ol", { class: "laycols" });
    const drawList = () =>
      list.replaceChildren(
        ...cols.map((c, i) =>
          el(
            "li",
            { class: "qrow" },
            el("label", { class: "ptask" }, c.keep),
            c.header,
            el(
              "span",
              { class: "rowacts" },
              ib("chevron-down", `Move ${c.header.value} down`, () => {
                if (i >= cols.length - 1) return;
                [cols[i], cols[i + 1]] = [cols[i + 1] as (typeof cols)[number], c];
                drawList();
              }),
            ),
          ),
        ),
      );
    drawList();
    const go = el("button", { type: "button", class: "btn pri" }, "Save layout");
    const m = openModal({
      title: "Add a layout",
      sub: `Starts from ${from.name}: rename, reorder or drop columns for the payroll company. It becomes the company's layout.`,
      body: [el("div", { class: "form", style: "max-width:none" }, name.el, list)],
      actions: [go],
    });
    name.input.focus();
    go.addEventListener("click", async () => {
      go.disabled = true;
      const r = await api<{ layout: { id: string } }>("POST", "/api/payroll/layouts", {
        fromId: v.layoutId,
        name: name.input.value,
        columns: cols
          .filter((c) => c.keep.checked)
          .map((c) => ({ source: c.source, header: c.header.value })),
      });
      go.disabled = false;
      if (!r.ok) return refuse(m.note, r.body.error);
      m.close();
      toast("Layout saved", "It's the payroll file layout now");
      void load();
    });
  }

  function draw(v: Run) {
    week = v.week.start;
    const summarySlot = el("div");
    const p = v.period;
    const status = p?.status ?? "none";
    // The head's Send to payroll (mockup), once the run is approved.
    const send = el(
      "button",
      { type: "button", class: "btn pri" },
      icon("paper-airplane"),
      " Send to payroll",
    );
    send.addEventListener("click", () => sendToPayroll(v));
    head.querySelector(".actions")?.replaceChildren(...(v.can.export ? [send] : []));
    // The stage card's next step.
    const main: HTMLElement[] = [];
    const button = (label: string, run: () => void, pri = true) => {
      const b = el("button", { type: "button", class: `btn${pri ? " pri" : ""}` }, label);
      b.addEventListener("click", () => {
        b.disabled = true;
        Promise.resolve(run()).finally(() => (b.disabled = false));
      });
      main.push(b);
    };
    if (v.can.build)
      button(
        p ? "Rebuild" : "Build the run",
        () =>
          void step(
            "/api/payroll/build",
            p ? "Rebuilt" : "Run built",
            "From the week's validated visits",
            { week: v.week.start },
          ),
        !p,
      );
    if (v.can.lock && p)
      button(
        "Lock hours",
        () =>
          void step(
            `/api/payroll/${p.id}/lock`,
            "Hours locked",
            "Later corrections go to the next run",
          ),
      );
    if (v.can.approve && p)
      button("Approve", () => void step(`/api/payroll/${p.id}/approve`, "Approved"));
    if (v.can.paid && p)
      button("Mark paid", () => void step(`/api/payroll/${p.id}/paid`, "Marked paid"));
    const fileLink = p?.exported
      ? (() => {
          const a = el(
            "button",
            { type: "button", class: "btn sm" },
            icon("download"),
            ` ${p.exported.name}`,
          );
          a.addEventListener("click", async () => {
            const res = await fetch(`/api/payroll/${p.id}/file`, { cache: "no-store" });
            if (!res.ok) return toast("That file couldn't be downloaded");
            saveText(p.exported?.name ?? "payroll.csv", await res.text());
          });
          return a;
        })()
      : null;
    const stage = el(
      "section",
      { class: "card stagebox", "aria-label": "Stage" },
      el(
        "div",
        { class: "cardhead", style: "margin:0" },
        el(
          "div",
          {},
          el("div", { class: "eyebrow" }, `${v.week.label}${p ? ` · ${p.words}` : ""}`),
          el("h2", {}, p ? HEADING[p.status] : "No run yet"),
        ),
        el(
          "span",
          { class: "rowacts" },
          ib("arrow-left", "Last week", () => {
            week = plus(v.week.start, -1);
            void load();
          }),
          ib("arrow-right", "Next week", () => {
            week = plus(v.week.end, 1);
            void load();
          }),
        ),
      ),
      el(
        "ol",
        { class: "stepper" },
        ...v.stages.map((s, i) =>
          el(
            "li",
            { class: p && i < p.stage ? "done" : p && i === p.stage ? "cur" : undefined },
            el("span", { class: "bar" }),
            el("span", { class: "lab" }, el("span", { class: "n" }, String(i + 1)), s),
          ),
        ),
      ),
      el(
        "div",
        { class: "nextstep" },
        el(
          "p",
          {},
          `${NEXT[status] ?? ""}${p ? ` ${p.built}.` : v.ended ? "" : " This week hasn't ended yet."}`,
        ),
        el("span", { class: "rowacts" }, ...main, fileLink ?? ""),
      ),
    );
    const cols: { h: string; f: (r: Row) => Node | string; num?: boolean; id?: string }[] = [
      { h: "Worker", id: "worker", f: (r) => (r.worker ? el("b", {}, r.worker) : "") },
      { h: "Type", f: (r) => r.type },
      {
        h: "Where",
        f: (r) =>
          el(
            "span",
            {},
            r.where,
            ...r.notes.map((x) => el("div", { class: "small muted" }, x)),
            r.held ? el("div", {}, chip(`Held · ${r.held.reason}`, "warn")) : "",
          ),
      },
      { h: "Hours", num: true, f: (r) => r.hours },
      { h: "Pay rate", num: true, f: (r) => r.rate },
      { h: "Regular", num: true, f: (r) => r.regular },
      { h: "OT", num: true, f: (r) => r.overtime },
      { h: "Bonus", num: true, f: (r) => r.bonus },
      { h: "Reimbursements", num: true, f: (r) => r.reimbursements },
      {
        h: "Gross",
        num: true,
        f: (r) =>
          el(
            "span",
            {},
            el("b", {}, r.gross),
            r.adjustments
              ? el("div", { class: "small muted" }, `of it ${r.adjustments} correction`)
              : "",
          ),
      },
    ];
    if (v.can.hold)
      cols.push({
        h: "",
        f: (r) => {
          if (!r.worker) return "";
          if (r.held)
            return ib("unlock", `Release ${r.name}`, () => {
              void step(`/api/payroll/${p?.id}/release`, `${r.name} released`, undefined, {
                personId: r.personId,
              });
            });
          return ib("circle-slash", `Hold ${r.name}`, () => holdModal(v, r));
        },
      });
    body.replaceChildren(
      stage,
      v.rows.length || ("total" in v && v.total)
        ? // Brief 1E-32: the run's rows one page at a time, with search.
          list<Row>({
            id: "payroll/run",
            userId: ctx.me.user.id,
            noun: "rows",
            caption: "Payroll run",
            searchLabel: "Search people or clients",
            first: "total" in v ? { rows: v.rows, page: v as Run & PageInfo } : undefined,
            source: async (params) => {
              if (week) params.set("week", week);
              const r = await api<Run & PageInfo>("GET", `/api/payroll?${params.toString()}`);
              return r.ok ? { rows: r.body.rows, page: r.body } : null;
            },
            rowId: (r) => `${r.personId}:${r.where}:${r.type}:${r.hours}:${r.gross}`,
            columns: cols.map((c, i) => ({
              id: c.id ?? `c${i}`,
              label: c.h,
              num: c.num,
              text: (r: Row) => (c.id === "worker" ? r.name : ""),
              cell: c.f,
            })),
          })
        : el(
            "p",
            { class: "muted" },
            p
              ? "No validated visits in this week."
              : "No run for this week yet. It builds itself the morning after the week ends.",
          ),
      v.rows.length
        ? el(
            "p",
            { class: "small muted" },
            `Total ${v.totals.gross} · ${v.totals.regularHours} regular hours · ${v.totals.overtimeHours} overtime hours${v.totals.held ? ` · ${v.totals.held} held` : ""}`,
          )
        : "",
      el("p", { class: "small muted" }, `Overtime: ${v.overtime}`),
      summarySlot,
      el("div", { class: "grid2" }, fileCard(v), mileCard(v)),
    );
    const at = v.week.start;
    void summaryCard(at).then((c) => {
      if (week === at) summarySlot.replaceChildren(...(c ? [c] : []));
    });
  }

  /** The Payroll file card (mockup v58): the chosen layout's columns; pick or add a layout. */
  function fileCard(v: Run): HTMLElement {
    const ADD = "__add";
    const pick = el(
      "select",
      { "aria-label": "Payroll file layout", disabled: !v.can.addLayout },
      ...v.layouts.map((l) => el("option", { value: l.id, selected: l.id === v.layoutId }, l.name)),
      ...(v.can.addLayout ? [el("option", { value: ADD }, "Add a layout…")] : []),
    );
    pick.addEventListener("change", async () => {
      if (pick.value === ADD) {
        pick.value = v.layoutId ?? "";
        return layoutModal(v);
      }
      const r = await api("PUT", "/api/settings/payroll", { layoutId: pick.value });
      if (!r.ok) return toast("That wasn't saved", (r.body as { error?: string }).error);
      toast("Payroll file layout saved");
      void load();
    });
    return card(
      el("span", {}, icon("download"), " Payroll file"),
      "FlowCare's standard file. Any payroll company that imports a spreadsheet can use it.",
      [
        pick,
        ...(v.can.addLayout
          ? [
              ib(
                "plus",
                "Add a layout: rename, reorder or drop columns for the payroll company",
                () => layoutModal(v),
              ),
            ]
          : []),
      ],
      v.fileLayout
        ? table(
            [
              { h: "Column", f: (c) => el("b", {}, c.header) },
              { h: "What's in it", f: (c) => c.what },
            ],
            pairedColumns(v.fileLayout.columns),
            undefined,
            "Payroll file columns",
          )
        : el("p", { class: "muted" }, "No layout yet."),
      el("p", { class: "small muted" }, `${v.fileLayout?.rows ?? ""}`),
    );
  }

  /** The Mileage card (mockup v58): paid to staff, and charged to clients (set per client). */
  function mileCard(v: Run): HTMLElement {
    const rows: [string, string, string, string][] = [
      ["Pay rate a mile", v.mileageCard.rate, v.mileageCard.from, v.mileageCard.irs],
      [
        "Bill rate a mile",
        "Set per client",
        "–",
        "On each client's Billing card, for example $1.00 a mile for errands, or not billed",
      ],
    ];
    return card(
      el("span", {}, icon("location"), " Mileage"),
      null,
      v.can.editMileage
        ? [ib("pencil", "Change the pay rate a mile from a date", mileageModal)]
        : [],
      table(
        [
          { h: "", f: (r) => el("b", {}, r[0]) },
          { h: "A mile", f: (r) => r[1] },
          { h: "From", f: (r) => r[2] },
          { h: "", f: (r) => el("span", { class: "small muted" }, r[3]) },
        ],
        rows,
        undefined,
        "Mileage",
      ),
    );
  }

  await load();
}

/** Pairs that read as one row on the card, as the mockup shows them (Week, names, overtime). */
const PAIRS: [string, string, string | null][] = [
  ["last_name", "first_name", null],
  ["week_start", "week_end", "Week"],
  ["overtime_hours", "overtime_pay", null],
];

/** The file's columns for the card: a pair next to each other in the file reads as one row. */
function pairedColumns(cols: readonly { header: string; source: string; what: string }[]) {
  const out: { header: string; what: string }[] = [];
  for (let i = 0; i < cols.length; i++) {
    const c = cols[i] as (typeof cols)[number];
    const next = cols[i + 1];
    const pair = PAIRS.find(([a, b]) => a === c.source && b === next?.source);
    if (pair && next) {
      out.push({ header: pair[2] ?? `${c.header}, ${next.header}`, what: c.what });
      i++;
    } else out.push({ header: c.header, what: c.what });
  }
  return out;
}
